import { useCallback, useState, useEffect } from "react";
import { Accept, FileWithPath, useDropzone } from "react-dropzone";
import { Button } from "@/components/ui";
import { convertFileToUrl } from "@/lib/utils";
import { Video, X } from "lucide-react";

type FileUploaderProps = {
  fieldChange: (files: File[]) => void;
  mediaUrl: string | string[];
  mode?: 'post' | 'message' | 'video';
  maxFiles?: number;
  accept?: Accept;
};

const FileUploader = ({ 
  fieldChange, 
  mediaUrl = "", 
  mode = "post", 
  maxFiles = mode === "post" ? 6 : mode === "video" ? 1 : 30,
  accept = {
    'image/*': mode === "video" ? [] : ['.png', '.jpeg', '.jpg'],
    'video/*': mode === 'post' ? ['.mp4', '.mov'] : mode === 'video' ? ['.mp4', '.mov', '.webm'] : ['.mp4', '.mov']
  }
}: FileUploaderProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [fileUrls, setFileUrls] = useState<string[]>([]);

  useEffect(() => {
    if (mediaUrl) {
      setFileUrls(Array.isArray(mediaUrl) ? mediaUrl : [mediaUrl]);
    }
  }, [mediaUrl]);

  const onDrop = useCallback(async (acceptedFiles: FileWithPath[]) => {
    let newFiles = [...files];
    
    for (const file of acceptedFiles) {
      // For video mode, only allow videos and replace existing file
      if (mode === "video") {
        if (!file.type.startsWith("video/")) {
          alert("Only video files are allowed");
          continue;
        }

        // Video validation for duration
        const isValid = await new Promise<boolean>((resolve) => {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.src = URL.createObjectURL(file);
          video.onloadedmetadata = () => {
            resolve(video.duration <= 60);
            URL.revokeObjectURL(video.src);
          };
          video.onerror = () => {
            resolve(false);
            URL.revokeObjectURL(video.src);
          };
        });

        if (!isValid) {
          alert("Videos must be 60 seconds or less");
          continue;
        }

        // Replace existing file for video mode
        newFiles = [file];
        break;
      }
      // For post mode, if a video is being added, clear all existing files
      else if (mode === "post" && file.type.startsWith("video/")) {
        // Video validation
        const isValid = await new Promise<boolean>((resolve) => {
          const video = document.createElement("video");
          video.preload = "metadata";
          video.src = URL.createObjectURL(file);
          video.onloadedmetadata = () => {
            resolve(video.duration <= 60);
            URL.revokeObjectURL(video.src);
          };
          video.onerror = () => {
            resolve(false);
            URL.revokeObjectURL(video.src);
          };
        });

        if (!isValid) {
          alert("Videos must be 60 seconds or less");
          continue;
        }

        // Clear all existing files for post mode when adding a video
        newFiles = [file];
        break; // Only add this video and stop processing other files
      }
      // For post mode, if adding images and there's already a video, don't allow
      else if (mode === "post" && newFiles.some(f => f.type.startsWith("video/"))) {
        alert("Video posts can't contain additional images");
        continue;
      }
      // For message mode or if adding more images to post with existing images
      else {
        if (newFiles.length >= maxFiles) {
          alert(`Maximum ${maxFiles} files allowed`);
          break;
        }
        newFiles.push(file);
      }
    }

    setFiles(newFiles);
    setFileUrls(newFiles.map(convertFileToUrl));
    fieldChange(newFiles);
  }, [files, maxFiles, mode, fieldChange]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept,
    multiple: mode !== "video", // Only allow single file for video mode
    maxFiles,
  });

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    setFileUrls(newFiles.map(convertFileToUrl));
    fieldChange(newFiles);
  };

  // Get display text based on mode
  const getDisplayText = () => {
    switch (mode) {
      case "video":
        return {
          title: "Drag video here",
          subtitle: "MP4, MOV, WebM (60s max, 100MB max)",
          button: "Select video file"
        };
      case "post":
        return {
          title: "Drag photos or video here",
          subtitle: "SVG, PNG, JPG, MP4 (60s max)",
          button: "Select from device"
        };
      case "message":
      default:
        return {
          title: "Drag files here",
          subtitle: "Images, videos, documents",
          button: "Select from device"
        };
    }
  };

  const displayText = getDisplayText();

  return (
    <div {...getRootProps()} className="cursor-pointer">
      <input {...getInputProps()} className="cursor-pointer" />
      
      <div className={`flex ${mode === "post" || mode === "video" ? "flex-col gap-4" : "flex-wrap gap-2"}`}>
        {fileUrls.map((url, index) => (
          <div key={index} className="relative">
            {files[index]?.type?.startsWith("video/") || (typeof url === "string" && url.includes("video")) ? (
              mode === "post" || mode === "video" ? (
                <video 
                  controls 
                  src={url} 
                  className={`rounded-xl ${mode === "video" ? "max-h-[300px] w-full" : "max-h-[400px]"}`}
                />
              ) : (
                <div className="w-16 h-16 bg-dark-3 flex-center rounded-lg">
                  <Video className="text-light-1" />
                </div>
              )
            ) : (
              <img 
                src={url} 
                alt="file" 
                className={mode === "post" || mode === "video" ? "file_uploader-img" : "w-16 h-16 object-cover rounded-lg"} 
              />
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeFile(index);
              }}
              className="absolute top-1 right-1 bg-red rounded-full p-1 z-10"
            >
              <X className="h-3 w-3 text-white" />
            </button>
          </div>
        ))}
      </div>

      {fileUrls.length === 0 && (
        <div className="file_uploader-box">
          <img 
            src="/assets/icons/file-upload.svg" 
            width={96} 
            height={77} 
            alt="file upload" 
          />
          <h3 className="base-medium text-light-2 mb-2 mt-6">
            {displayText.title}
          </h3>
          <p className="text-light-4 small-regular mb-6">
            {displayText.subtitle}
          </p>
          <Button type="button" className="shad-button_dark_4">
            {displayText.button}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUploader;